import { useState, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import { UserDetails } from '../types/User';
import { Document, DocumentType } from '../types/Document';
import { calculateOverallCompletion } from '../utils/completionCalculations';
import { useEnhancedAuth } from '../contexts/EnhancedAuthContext';
import { DOCUMENT_ENDPOINTS, AGENCY_ENDPOINTS, EDUCATION_ENDPOINTS, STORAGE_KEYS } from '../constants/api';

export interface ManagerUserData {
	user: UserDetails;
	documents: Document[];
	fieldValues: { [key: string]: any };
	stats: any; // Optionally add stats here
}

export function useManagerData() {
	const [agencyUsers, setAgencyUsers] = useState<ManagerUserData[]>([]);
	const [agencyDocumentMap, setAgencyDocumentMap] = useState<any>({});
	// Company document data
	const [companyDocuments, setCompanyDocuments] = useState<Document[]>([]);
	const [companyFieldValues, setCompanyFieldValues] = useState<{ [key: string]: any }>({});
	// Flattened company structure data (compatible with useDocumentMap helper methods)
	const [companyCategories, setCompanyCategories] = useState<any[]>([]);
	const [companySubcategories, setCompanySubcategories] = useState<any[]>([]);
	const [companyDocumentTypes, setCompanyDocumentTypes] = useState<any[]>([]);
	const [companyDocumentFields, setCompanyDocumentFields] = useState<any[]>([]);
	const [roles, setRoles] = useState<any[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Enhanced auth migration with type compatibility
	const { userDetails: enhancedUserDetails } = useEnhancedAuth();
	
	// Transform enhanced user details to current format
	// Memoize to prevent infinite re-renders
	const userDetails = useMemo(() => {
		if (!enhancedUserDetails) return null;
		return {
			id: enhancedUserDetails.id,
			fullNames: enhancedUserDetails.fullNames ?? null,
			lastName: enhancedUserDetails.lastName ?? null,
			fullName: enhancedUserDetails.fullName,
			displayName: enhancedUserDetails.displayName,
			email: enhancedUserDetails.email,
			identificationType: enhancedUserDetails.identificationType ?? null,
			idNumber: enhancedUserDetails.idNumber ?? null,
			dateOfBirth: enhancedUserDetails.dateOfBirth ?? null,
			gender: enhancedUserDetails.gender ?? null,
			mobileNumber: enhancedUserDetails.mobileNumber ?? null,
			// Physical address fields
			unitNumber: enhancedUserDetails.unitNumber,
			complex: enhancedUserDetails.complex,
			streetNumber: enhancedUserDetails.streetNumber,
			streetName: enhancedUserDetails.streetName,
			suburb: enhancedUserDetails.suburb,
			city: enhancedUserDetails.city,
			postalCode: enhancedUserDetails.postalCode,
			fullPhysicalAddress: enhancedUserDetails.fullPhysicalAddress,
			// Postal address fields
			postalAddressSameAsResidential: enhancedUserDetails.postalAddressSameAsResidential,
			postalUnitNumber: enhancedUserDetails.postalUnitNumber ?? null,
			postalComplex: enhancedUserDetails.postalComplex ?? null,
			postalStreetNumber: enhancedUserDetails.postalStreetNumber ?? null,
			postalStreetName: enhancedUserDetails.postalStreetName ?? null,
			postalSuburb: enhancedUserDetails.postalSuburb ?? null,
			postalCity: enhancedUserDetails.postalCity ?? null,
			postalPostalCode: enhancedUserDetails.postalPostalCode ??  null,
			// Account
			accountStatus: enhancedUserDetails.accountStatus ?? null,
			profileComplete: enhancedUserDetails.profileComplete,
			emailVerified: enhancedUserDetails.emailVerified ?? false,
			agency: enhancedUserDetails.agency ?? null,
			role: enhancedUserDetails.role,
			roleName: enhancedUserDetails.role,
			permissions: enhancedUserDetails.permissions ?? [],
			accessLevel: enhancedUserDetails.accessLevel ?? 1,
			archived: enhancedUserDetails.archived,
		};
	}, [enhancedUserDetails]);

	const agencyId = userDetails?.agency?.isPaymentValid ? userDetails?.agency?.agencyId : undefined;

	// Helper function to flatten nested document map into flat arrays
	// Now handles both direct category items AND subcategory items
	const flattenDocumentMap = useCallback((mapData: any) => {
		if (!mapData) {
			return {
				categories: [],
				subcategories: [],
				documentTypes: [],
				documentFields: []
			};
		}

		const categories = mapData.categories || [];
		const subcategories: any[] = [];
		const documentTypes: any[] = [];
		const documentFields: any[] = [];

		categories.forEach((category: any) => {
			// Handle direct category items (no subcategory)
			if (category.documentTypes) {
				documentTypes.push(...category.documentTypes);
			}
			if (category.fields) {
				const normalizedFields = category.fields.map((f: any) => ({
					...f,
					name: f.name,
					required: typeof f.required === 'boolean' ? f.required : false,
				}));
				documentFields.push(...normalizedFields);
			}
			
			// Handle subcategory items
			if (category.subcategories) {
				subcategories.push(...category.subcategories);
				category.subcategories.forEach((subcategory: any) => {
					if (subcategory.documentTypes) {
						documentTypes.push(...subcategory.documentTypes);
					}
					if (subcategory.fields) {
						const normalizedFields = subcategory.fields.map((f: any) => ({
							...f,
							name: f.name,
							required: typeof f.required === 'boolean' ? f.required : false,
						}));
						documentFields.push(...normalizedFields);
					}
				});
			}
		});

		return { categories, subcategories, documentTypes, documentFields };
	}, []);

	const fetchAgentsDocumentMap = useCallback(async () => {
		try {
			const token = sessionStorage.getItem('token') || sessionStorage.getItem('jwt') || sessionStorage.getItem('accessToken');
			const res = await axios.get(DOCUMENT_ENDPOINTS.AGENT_MAP, {
				headers: token ? { Authorization: `Bearer ${token}` } : {}
			});
			
			// Extract roles from response
			if (res.data?.roles) {
				setRoles(res.data.roles);
			}
			
			return res.data || {};
		} catch (err: any) {
			console.error("Error fetching agent map:", err);
			setError('Failed to fetch agent map');
			return { categories: [], subcategories: [], documentTypes: [], fieldTypes: [], roles: [] };
		}
	}, []);

	const fetchAgencyDocumentMap = useCallback(async () => {
			try {
				const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN) || sessionStorage.getItem('jwt') || sessionStorage.getItem('accessToken');

				// Fetch agency structural map and templates in parallel so we can attach templates to document types
				const [mapRes, templatesRes] = await Promise.all([
					axios.get(DOCUMENT_ENDPOINTS.AGENCY_MAP, { headers: token ? { Authorization: `Bearer ${token}` } : {} }),
					axios.get(EDUCATION_ENDPOINTS.TEMPLATES, { headers: token ? { Authorization: `Bearer ${token}` } : {}, params: { userId: userDetails?.id } }).catch(() => ({ data: { templates: [] } }))
				]);

				// New API returns: { categories, subcategories, documentTypes, fieldTypes }
				// Flatten and store the company document structure
				const flattened = flattenDocumentMap(mapRes.data || {});

				const templates = templatesRes.data?.templates || [];

				// Build a map of templates keyed by documentTypeId
				const templateMap = new Map<string, any>();
				templates.forEach((template: any) => {
					if (!template.documentTypeId) return;
					const docTypeId = typeof template.documentTypeId === 'object' ? template.documentTypeId._id : template.documentTypeId;
					if (docTypeId) templateMap.set(docTypeId, template);
				});

				const documentTypesWithTemplates = (flattened.documentTypes || []).map((type: DocumentType) => ({
					...type,
					template: templateMap.get(type.id) || null,
					required: type.required,
					recurrence: type.recurrence || 'none',
					canExpire: (type.recurrence || 'none') !== 'none',
				}));

				setCompanyCategories(flattened.categories);
				setCompanySubcategories(flattened.subcategories);
				setCompanyDocumentTypes(documentTypesWithTemplates);
				setCompanyDocumentFields(flattened.documentFields);
				
				// Extract roles from response
				if (mapRes.data?.roles) {
					setRoles(mapRes.data.roles);
				}
            
				// Keep the original nested structure for compatibility
				setAgencyDocumentMap({ categories: flattened.categories });
            
				return mapRes.data || {};
			} catch (err: any) {
				console.error("Error fetching agency map:", err);
				setError('Failed to fetch agency map');
				return { categories: [], subcategories: [], documentTypes: [], fieldTypes: [] };
			}
		}, [flattenDocumentMap, userDetails?.id]);

	// Fetch company documents (agency-level documents, no specific user)
	const fetchCompanyDocuments = useCallback(async (documentTypes: any[], agencyId: string) => {
		if (!documentTypes.length || !agencyId) {
			setCompanyDocuments([]);
			return;
		}

		try {
			const token = sessionStorage.getItem('token');
			const response = await axios.get(DOCUMENT_ENDPOINTS.COMPANY_DOCUMENTS_BY_IDS, {
				params: { 
					typeIDs: documentTypes.map((dt: any) => dt.id), 
					agencyID: agencyId,
					// No userID parameter - this fetches company-level documents
				},
				headers: token ? { Authorization: `Bearer ${token}` } : {}
			});
			
			setCompanyDocuments(response.data || []);
		} catch (err: any) {
			console.error('Error fetching company documents:', err);
			setError('Failed to fetch company documents');
			setCompanyDocuments([]);
		}
	}, []);

	// Fetch company field values (agency-level field values, no specific user)
	const fetchCompanyFieldValues = useCallback(async (fieldTypes: any[], agencyId: string) => {
		if (!fieldTypes.length || !agencyId) {
			setCompanyFieldValues({});
			return;
		}

		try {
			const token = sessionStorage.getItem('token') || sessionStorage.getItem('jwt') || sessionStorage.getItem('accessToken');
			const fieldIDs = fieldTypes.map((ft: any) => ft.id);
			
			// Always use POST to avoid URL length limits with many field IDs
			const response = await axios.post(
				DOCUMENT_ENDPOINTS.COMPANY_FIELD_VALUES_BY_IDS,
				{
					fieldIDs,
					agencyID: agencyId,
				},
				{
					headers: token ? { Authorization: `Bearer ${token}` } : {}
				}
			);
			
			const values: { [key: string]: any } = {};
			(response.data || []).forEach((item: any) => {
				values[item.fieldID] = item.fieldValue || item.value;
			});
			
			setCompanyFieldValues(values);
		} catch (err: any) {
			console.error('Error fetching company field values:', err);
			setError('Failed to fetch company field values');
			setCompanyFieldValues({});
		}
	}, []);

	// Fetch Agents Document Map, Agency Users, their Documents and Field Values
	const fetchAgencyUsersData = useCallback(async (agencyId: string) => {
		setLoading(true);
		setError(null);
		try {
			const token = sessionStorage.getItem('token') || sessionStorage.getItem('jwt') || sessionStorage.getItem('accessToken');
			const agencyMapData = await fetchAgencyDocumentMap();
			const agentMapData = await fetchAgentsDocumentMap();
			// Extract data from agency map (for company documents)
			const companyDocumentTypes = agencyMapData?.documentTypes || [];
			const companyFieldTypes = agencyMapData?.fieldTypes || [];

			await Promise.all([
				fetchCompanyDocuments(companyDocumentTypes, agencyId),
				fetchCompanyFieldValues(companyFieldTypes, agencyId)
			]);

			let users: UserDetails[] = [];
			try {
				const usersRes = await axios.get(AGENCY_ENDPOINTS.USERS(agencyId), {
					headers: token ? { Authorization: `Bearer ${token}` } : {}
				});
				users = usersRes.data || [];
			} catch {
				setAgencyUsers([]);
				return;
			}

			const { categories, subcategories, documentTypes, fieldTypes } = agentMapData || {};

			const userData: ManagerUserData[] = await Promise.all(users.map(async (user) => {
				if (!documentTypes || documentTypes.length === 0) {
					return { 
						user, 
						documents: [], 
						fieldValues: {}, 
						stats: {
							categories: {},
							subcategories: {},
							overall: { totalItems: 0, completedItems: 0, missingItems: 0, expiredItems: 0, completionRate: 0 }
						}
					};
				}

				const docsRes = await axios.get(DOCUMENT_ENDPOINTS.DOCUMENTS_BY_IDS, {
					params: { typeIDs: documentTypes.map((dt: any) => dt.id), userID: user.id, agencyID: agencyId },
					headers: token ? { Authorization: `Bearer ${token}` } : {}
				});
				
				let fieldValues: { [key: string]: any } = {};
				if (fieldTypes && fieldTypes.length > 0) {
					const fieldValuesRes = await axios.post(
						DOCUMENT_ENDPOINTS.FIELD_VALUES_BY_IDS,
						{ 
							fieldIDs: fieldTypes.map((ft: any) => ft.id), 
							userID: user.id, 
							agencyID: agencyId 
						},
						{
							headers: token ? { Authorization: `Bearer ${token}` } : {}
						}
					);
					(fieldValuesRes.data || []).forEach((item: any) => {
						fieldValues[item.fieldID] = item.fieldValue || item.value;
					});
				}

				const documents: Document[] = docsRes.data || [];

				const stats = calculateOverallCompletion(
					categories || [],
					subcategories || [],
					documentTypes || [],
					fieldTypes || [],
					documents,
					fieldValues
				);

				return { user, documents, fieldValues, stats };
			}));
			setAgencyUsers(userData);
		} catch (err: any) {
			console.error("Error fetching agency users, documents, or field values:", err);
			setError('Failed to fetch agency users data');
			setAgencyUsers([]);
		} finally {
			setLoading(false);
		}
	}, [fetchAgencyDocumentMap, fetchAgentsDocumentMap, fetchCompanyDocuments, fetchCompanyFieldValues]);

	const refetch = useCallback(() => {
		if (agencyId) fetchAgencyUsersData(agencyId);
	}, [agencyId, fetchAgencyUsersData]);

	useEffect(() => {
		if (agencyId) fetchAgencyUsersData(agencyId);
	}, [agencyId, fetchAgencyUsersData]);

	// Helper methods for filtering company document data (similar to useDocumentMap)
	
	// Company document upload function
	const companyDocumentUpload = useCallback(async (formData: FormData) => {
		try {
			// Don't set global loading state - let the upload form handle its own loading state
			const token = sessionStorage.getItem('token') || sessionStorage.getItem('jwt') || sessionStorage.getItem('accessToken');
			const response = await axios.post(DOCUMENT_ENDPOINTS.UPLOAD, formData, {
				headers: {
					'Content-Type': 'multipart/form-data',
					...(token ? { Authorization: `Bearer ${token}` } : {}),
				},
			});

			// Add new document to local state for optimistic updates
			const newDocument = response.data;
			if (newDocument) {
				setCompanyDocuments(prev => [...prev, newDocument]);
			}

			return { success: true, newDocument };
		} catch (err: any) {
			console.error('Company document upload failed:', err);
			const message = err?.response?.data?.error || err?.message || 'Upload failed';
			return { success: false, error: message };
		}
	}, []);

	// Company document remove function
	const companyDocumentRemove = useCallback(async (docID: string) => {
		try {
			// Don't set global loading state - let the component handle its own loading state
			const token = sessionStorage.getItem('token') || sessionStorage.getItem('jwt') || sessionStorage.getItem('accessToken');
			await axios.post(DOCUMENT_ENDPOINTS.DELETE, 
				{ docID },
				{
					headers: token ? { Authorization: `Bearer ${token}` } : {}
				}
			);

			// Remove document from local state for optimistic updates
			setCompanyDocuments(prev => prev.filter(doc => doc.id !== docID));

			return { success: true };
		} catch (err: any) {
			console.error('Company document removal failed:', err);
			const message = err?.response?.data?.error || err?.message || 'Removal failed';
			return { success: false, error: message };
		}
	}, []);
	
	// Company field management functions
	const saveCompanyFieldValues = useCallback(async (values: { [key: string]: any }) => {
		try {
			const token = sessionStorage.getItem('token') || sessionStorage.getItem('jwt') || sessionStorage.getItem('accessToken');
			const agencyID = userDetails?.agency?.agencyId;
			
			if (!agencyID) {
				return { success: false, error: 'No agency selected' };
			}
			
			await axios.post(
				DOCUMENT_ENDPOINTS.COMPANY_FIELD_VALUES_SAVE,
				{
					values,
					agencyID,
				},
				{
					headers: token ? { Authorization: `Bearer ${token}` } : {}
				}
			);
			
			// Update local state
			setCompanyFieldValues(prev => ({ ...prev, ...values }));
			return { success: true };
		} catch (error) {
			console.error('Failed to save company field values:', error);
			return { success: false, error: 'Failed to save company field values' };
		}
	}, [userDetails?.agency?.agencyId]);

	const fetchCompanyFieldValuesForFields = useCallback(async (fieldIds: string[]) => {
		// The fetchCompanyFieldValues function is already called in useEffect
		// This is just a wrapper for compatibility with the component interface
		return;
	}, []);

	const getCompanySubcategoriesForCategory = useCallback((categoryId: string) => {
		return companySubcategories.filter(sub => sub.category_id === categoryId);
	}, [companySubcategories]);
	
	const getCompanyDocumentTypesForSubcategory = useCallback((subcategoryId: string) => {
		return companyDocumentTypes.filter(type => type.subcategory_id === subcategoryId);
	}, [companyDocumentTypes]);
	
	const getCompanyDocumentTypesForCategory = useCallback((categoryId: string) => {
		// Get direct category items (no subcategory)
		return companyDocumentTypes.filter(type => 
			type.category_id === categoryId && !type.subcategory_id
		);
	}, [companyDocumentTypes]);
	
	const getCompanyFieldsForSubcategory = useCallback((subcategoryId: string) => {
		return companyDocumentFields.filter(field => field.subcategory_id === subcategoryId);
	}, [companyDocumentFields]);
	
	const getCompanyFieldsForCategory = useCallback((categoryId: string) => {
		// Get direct category items (no subcategory)
		return companyDocumentFields.filter(field => 
			field.category_id === categoryId && !field.subcategory_id
		);
	}, [companyDocumentFields]);

	return { 
		agencyUsers, 
		agencyDocumentMap,
		companyDocuments, 
		companyFieldValues,
		// Flattened company structure data
		companyCategories,
		companySubcategories,
		companyDocumentTypes,
		companyDocumentFields,
		roles,
		// Helper methods for company documents
		getCompanySubcategoriesForCategory,
		getCompanyDocumentTypesForSubcategory,
		getCompanyDocumentTypesForCategory,
		getCompanyFieldsForSubcategory,
		getCompanyFieldsForCategory,
		// Company document operations
		companyDocumentUpload,
		companyDocumentRemove,
		// Company field management functions
		saveCompanyFieldValues,
		fetchCompanyFieldValues: fetchCompanyFieldValuesForFields,
		loading, 
		error, 
		fetchAgencyUsersData, 
		refetch 
	};
};
